import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { parseUnits } from 'ethers'
import { InfoIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { fetchNft } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import GradientWrapper from '@/common/GradientWrapper'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatDurationFromSeconds,
  getNftTopUpExtraPeriods,
} from '@/helpers'
import { buildTopUpMsg, TopUpError } from '@/libs/keplr/messages'
import { invalidateNftScope } from '@/queries/invalidate'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

const topUpSchema = z.object({
  amount: z
    .string()
    .min(1, 'Amount is required')
    .regex(/^\d+(?:\.\d{0,18})?$/, 'Enter a valid amount')
    .refine(value => {
      try {
        return parseUnits(value, 18) > 0n
      } catch {
        return false
      }
    }, 'Enter an amount greater than 0'),
})

type TopUpFormValues = z.infer<typeof topUpSchema>

export default function TopUpModal({
  open,
  onClose,
  nftAddress,
}: {
  open: boolean
  onClose: () => void
  nftAddress: string
}) {
  const { signAndBroadcast, address, nftModuleParams } = useKeplrWallet()
  const queryClient = useQueryClient()

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isValid, errors },
  } = useForm<TopUpFormValues>({
    resolver: zodResolver(topUpSchema),
    mode: 'onChange',
    defaultValues: { amount: '' },
  })

  const amountValue = watch('amount')
  const denom = bridgelessChainInfo.stakeCurrency.coinMinimalDenom

  const { data: nftData } = useQuery({
    queryKey: queryKeys.nft.byAddress(nftAddress),
    enabled: open,
    queryFn: async () => fetchNft(nftAddress),
    staleTime: 15_000,
  })

  const extension = useMemo(() => {
    if (!nftData || !isValid) return null
    try {
      const periods = getNftTopUpExtraPeriods(
        nftData,
        parseUnits(amountValue, 18).toString(),
      )
      const blocks = periods * Number(nftModuleParams?.vesting_period ?? 0)
      return { periods, duration: formatDurationFromSeconds(blocks * 5) }
    } catch {
      return null
    }
  }, [nftData, isValid, amountValue, nftModuleParams?.vesting_period])

  const fundTx = useMutation({
    mutationKey: queryKeys.tx.fund(nftAddress),
    mutationFn: async (values: TopUpFormValues) => {
      if (!address) throw new Error('Wallet not connected')

      const amountAtomic = parseUnits(values.amount, 18).toString()
      if (BigInt(amountAtomic) <= 0n) throw new Error('Invalid amount')

      const msg = buildTopUpMsg({
        creator: address,
        address: nftAddress,
        amount: { denom, amount: amountAtomic },
      })

      const txHash = await signAndBroadcast([msg])
      return txHash
    },
    onSuccess: (txHash: string) => {
      emitter.emit('success', {
        title: 'Shard topped up',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      invalidateNftScope(queryClient, nftAddress)
      onClose()
    },
    onError: error => {
      ErrorHandler.process(TopUpError.fromUnknown(error))
    },
  })

  const isLoading = isSubmitting || fundTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isLoading && onClose()}
      title={<span className='text-foreground'>Top Up NFT</span>}
      content={
        <form
          onSubmit={handleSubmit(values => fundTx.mutate(values))}
          className='text-foreground mt-4 flex flex-col gap-4 p-1'
        >
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>NFT Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(nftAddress, 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={nftAddress} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>
              Amount (BRIDGE)
            </span>
            <GradientWrapper>
              <input
                value={amountValue}
                disabled={isLoading}
                onChange={e =>
                  setValue('amount', e.target.value, { shouldValidate: true })
                }
                maxLength={20}
                className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none'
                placeholder='0.1'
              />
            </GradientWrapper>
            {errors.amount && (
              <span className='text-xs text-red-400'>
                {errors.amount.message as string}
              </span>
            )}
          </div>

          <div className='text-foreground flex items-start gap-2 rounded-md bg-[#141622] p-3 text-xs'>
            <InfoIcon className='mt-0.5 size-4 shrink-0' />
            <span>
              Top up increases the Shard token amount. The unlock per period
              stays the same, so the vesting gets longer.
              {extension && extension.periods > 0 && (
                <>
                  {' '}
                  This top up adds{' '}
                  <span className='font-semibold'>
                    {extension.periods}{' '}
                    {extension.periods === 1 ? 'period' : 'periods'}
                    {extension.duration && ` (~${extension.duration})`}
                  </span>
                  .
                </>
              )}
            </span>
          </div>
        </form>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isLoading}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={isLoading || !isValid}
            onClick={handleSubmit(values => fundTx.mutate(values))}
          >
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Topping up…
              </span>
            ) : (
              'Top up'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
